import React from "react";
import ReportHeader from "./ReportHeader";

const TABS = [
  { id: "query", label: "Query" },
  { id: "chart", label: "Chart" },
  { id: "annotations", label: "Annotations" },
];

/**
 * Shared layout for Insight and Funnel reports.
 * Renders: Top bar (optional) | Header row (date + chart type + tabs) | Main (chart) + Right panel.
 *
 * @param {React.ReactNode} topBar - e.g. <AnalyticsTopBar breadcrumbs={...} onSave={...} onClose={...} />
 * @param {"insight" | "funnel"} reportType
 * @param {object} headerProps - selectedPreset, onTimeFilterChange, chartType, onChartTypeChange, theme
 * @param {string} activeTab - 'query' | 'chart' | 'annotations'
 * @param {(id: string) => void} onTabChange
 * @param {React.ReactNode} children - chart area (main content)
 * @param {React.ReactNode} rightPanel - Query/Chart/Annotations panel (e.g. QueryBuilderPanel)
 * @param {object} theme - analytics theme
 */
export default function ReportLayout({
  topBar,
  reportType,
  headerProps = {},
  activeTab,
  onTabChange,
  children,
  rightPanel,
  theme = {},
}) {
  const {
    selectedPreset,
    onTimeFilterChange,
    chartType,
    onChartTypeChange,
  } = headerProps;

  return (
    <div className="flex flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
      <div className="flex flex-col flex-1 min-w-0">
        {topBar}

        <div
          className="flex items-center justify-between border-b"
          style={{
            backgroundColor: "#ffffff",
            fontFamily: "Raleway, sans-serif",
            borderColor: theme.border || "#DDE2EE",
          }}
        >
          <div
            className="flex items-center gap-2 flex-1"
            style={{
              paddingLeft: "1rem",
              paddingRight: "1rem",
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem",
            }}
          >
            <ReportHeader
              reportType={reportType}
              selectedPreset={selectedPreset}
              onTimeFilterChange={onTimeFilterChange}
              chartType={chartType}
              onChartTypeChange={onChartTypeChange}
              theme={theme}
            />
          </div>

          <div className="flex items-end" style={{ paddingRight: "1rem" }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className="relative text-sm font-medium transition-colors"
                style={{
                  color: activeTab === tab.id ? "#0f172a" : "#475569",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "2px",
                  paddingBottom: "8px",
                  lineHeight: "1.2",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) e.currentTarget.style.color = "#0f172a";
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) e.currentTarget.style.color = "#475569";
                }}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span
                    className="absolute bottom-0"
                    style={{
                      height: "2px",
                      backgroundColor: "#4B57AA",
                      left: "16px",
                      right: "16px",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <main
            className="flex-1 min-w-0 overflow-auto flex flex-col"
            style={{ backgroundColor: "#ffffff" }}
          >
            {children}
          </main>
          {rightPanel}
        </div>
      </div>
    </div>
  );
}
